/**
 * rerun_job Tool - Re-dispatch an existing job, reusing its settings (#5933).
 *
 * Like create_job, this ONLY queues a new job — you MUST follow up with wait to
 * retrieve results. Every setting (URL, instructions, schema, device class,
 * durations, tester requirements, GitHub linkage) is copied from the source job
 * server-side; the fields below override individual settings. The canonical use
 * is `url` to re-point the identical test at a fresh build after a rework
 * invalidated the last run. Project/org and the `originalJobId` lineage are
 * always set server-side.
 */

import type { Tool } from '@modelcontextprotocol/sdk/types.js';
import type { DeviceClass, JobMetadata, OutputSchema, RerunTestJobRequest } from '@runhuman/shared';
import type { JobClient } from '../clients/job-client.js';

export interface RerunJobArgs {
  jobId: string;
  url?: string;
  commitSha?: string;
  description?: string;
  targetDurationMinutes?: number;
  deviceClass?: DeviceClass;
  outputSchema?: OutputSchema;
  metadata?: JobMetadata;
}

export const rerunJobToolDefinition: Tool = {
  name: 'rerun_job',
  description: `IMPORTANT: This ONLY re-dispatches and queues a NEW job from an existing one. It does NOT perform the test or return results. You MUST follow up with wait.

Re-runs an existing job, reusing every setting (URL, instructions, output schema, device class, durations, tester-pool requirements, GitHub linkage) from the source job. Provide overrides to change individual settings — the canonical use is \`url\` to re-point the identical test at a fresh build after a rework invalidated the previous run.

Returns a NEW jobId (the job is now QUEUED, not complete). Then call wait to poll for and retrieve the result, exactly as with create_job.`,
  inputSchema: {
    type: 'object',
    properties: {
      jobId: {
        type: 'string',
        description: 'The source job ID to re-run. Its settings are copied verbatim unless overridden below.',
      },
      url: {
        type: 'string',
        description: 'Override the URL under test — typically a fresh build of the same page.',
      },
      commitSha: {
        type: 'string',
        description: 'Override the commit SHA the test runs against (recorded on the new job).',
      },
      description: {
        type: 'string',
        description: 'Override the tester instructions.',
      },
      targetDurationMinutes: {
        type: 'number',
        description: 'Override the target duration in minutes. Range: 1-60.',
      },
      deviceClass: {
        type: 'string',
        enum: ['desktop', 'mobile', 'both'],
        description: 'Override the device class: "desktop", "mobile", or "both".',
      },
      outputSchema: {
        type: 'object',
        description: 'Override the structured-output JSON Schema extracted from the tester\'s response.',
      },
    },
    required: ['jobId'],
  },
};

export async function handleRerunJob(
  args: RerunJobArgs,
  jobClient: JobClient
): Promise<{ content: Array<{ type: 'text'; text: string }>; isError?: boolean }> {
  const { jobId, metadata: callerMetadata, ...overrides } = args;

  if (!jobId) {
    return {
      content: [{ type: 'text', text: 'jobId is required to re-run a job.' }],
      isError: true,
    };
  }

  try {
    const metadata: JobMetadata = {
      ...callerMetadata,
      source: 'mcp',
      sourceCreatedAt: new Date().toISOString(),
      mcp: {},
    };

    const body: RerunTestJobRequest = { ...overrides, metadata };
    const data = await jobClient.rerunJob(jobId, body);

    return {
      content: [{
        type: 'text',
        text: `Job RE-DISPATCHED (not complete yet!)

New Job ID: ${data.jobId}
Re-run of: ${jobId}

The test has been queued for a HUMAN tester. It is NOT complete yet.

REQUIRED NEXT STEPS:
1. Call wait(jobId: "${data.jobId}", timeoutSeconds: 30)
2. If not complete, call wait again with increasing timeoutSeconds until complete

DO NOT consider this task done until you have retrieved the results!`,
      }],
    };
  } catch (error) {
    const err = error as Error & { status?: number; code?: string };

    if (err.status === 402 || err.code === 'INSUFFICIENT_CREDITS' || err.code === 'INSUFFICIENT_BALANCE') {
      const manageLink = 'https://runhuman.com/dashboard/manage-plan?source=mcp&openModal=true';
      return {
        content: [{
          type: 'text',
          text: `Insufficient Credits

The organization doesn't have enough credits to re-run this test.

IMPORTANT: Tell the user they need to purchase more credits by visiting:
${manageLink}

Do not attempt to retry until the user confirms they have added credits.`,
        }],
        isError: true,
      };
    }

    return {
      content: [{
        type: 'text',
        text: `Failed to re-run job

Source Job ID: ${jobId}
Error: ${err.message}
${err.status ? `Status: ${err.status}` : ''}`,
      }],
      isError: true,
    };
  }
}
